<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { tick } from "svelte";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { getNewCodePath } from "@core/schema/paths/getNewCodePath.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { expectErrorMessage } from "@core/utils/expectErrorMessage.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./SignIn.translations.json" with { type: "json" };
	import SignInWithGoogle from "./SignInWithGoogle.svelte";

	let mode = $state<"login" | "new-password">("login");

	let {
		/* eslint-disable prefer-const */
		searchParams,
		/* eslint-enable prefer-const */
		email = $bindable(searchParams?.get("email") ?? undefined),
	}: { searchParams?: URLSearchParams; email?: string } = $props();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const signIn = createPromiseWithStatus(async (email: string, password: string, newPassword?: string) => {
		const response =
			newPassword === undefined
				? await Auth.signIn(email, password)
				: await Auth.signInWithNewPassword(email, password, newPassword);

		if (response.SignedIn) {
			Auth.closeAuthenticationModal();
		} else if (response.Challenge === "NEW_PASSWORD_REQUIRED") {
			mode = "new-password";
		} else if (response.Challenge === "PASSWORD_RESET") {
			const error = new Error("Password reset required");
			error.name = "PasswordResetRequiredException";
			throw error;
		} else if (response.Challenge === "LEGACY_CUSTOMER") {
			await tick();
			await Auth.openAuthenticationModal(AuthenticationModalKind.ForgotPassword, { isLegacy: true, email });
		} else if (response.Challenge) {
			const error = new Error(response.Challenge);
			error.name = "ChallengeException";
			throw error;
		}
	});

	const isDesktop = createReactiveBreakpoint(">md");

	async function handleSignIn(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		const formData = new FormData(form);

		const password = formData.get("password") as string;
		const email = formData.get("email") as string;
		let newPassword: string | undefined;

		const newPasswordInput = form.elements.namedItem("newPassword");
		const confirmNewPasswordInput = form.elements.namedItem("confirmNewPassword");

		if (newPasswordInput instanceof HTMLInputElement && confirmNewPasswordInput instanceof HTMLInputElement) {
			newPassword = newPasswordInput.value;

			const confirmNewPassword = confirmNewPasswordInput.value;
			confirmNewPasswordInput.setCustomValidity("");

			if (newPassword !== confirmNewPassword) {
				confirmNewPasswordInput.setCustomValidity(translate("passwordsDoNotMatch"));
				confirmNewPasswordInput.reportValidity();
				return;
			}
		}

		await signIn.call(email, password, newPassword);
	}

	const error = $derived(
		signIn.value.status === "error" &&
			expectErrorMessage(
				signIn.value.error,
				tenant.locale,
				{
					NotAuthorizedException: translate("invalidCredentials"),
					UserNotFoundException: translate("userNotFound"),
					ChallengeException: ({ message }) => translate("challengeError", { code: message }),
					PasswordResetRequiredException: translate("passwordResetError"),
					UserNotConfirmedException: translate("userNotConfirmed", { url: getNewCodePath(email) }),
				},
				translate("unspecifiedError"),
			),
	);
</script>

<Form class="flex w-full flex-col items-stretch gap-2 mt-5 md:mt-10" onsubmit={handleSignIn}>
	<SignInWithGoogle />
	<div class="text-fcrgray text-sm/6 md:text-base text-center">
		<Formatted text={translate("or")} />
	</div>
	<Input autocomplete="email" required placeholder={translate("email")} type="email" name="email" bind:value={email} />
	<Input autocomplete="current-password" required placeholder={translate("password")} type="password" name="password" />
	{#if mode === "new-password"}
		<Input
			autocomplete="new-password"
			minlength={7}
			required
			placeholder={translate("newPassword")}
			type="password"
			name="newPassword"
		/>
		<Input
			autocomplete="new-password"
			minlength={7}
			required
			placeholder={translate("newPasswordAgain")}
			type="password"
			name="confirmNewPassword"
		/>
	{/if}
	<button
		type="button"
		class="text-sm/6 self-end py-4 mb-1.5 md:mb-4 md:text-base text-fcrgray underline"
		onclick={async (): Promise<void> => {
			await Auth.openAuthenticationModal(AuthenticationModalKind.ForgotPassword, { isLegacy: false });
		}}
	>
		<Formatted text={translate("forgotPassword")} />
	</button>
	<Button
		loading={signIn.value.status === "loading"}
		variant={ButtonVariant.Dark}
		size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
	>
		<Formatted text={translate("signInButton")} />
	</Button>

	{#if error}
		<p
			class="text-sm text-fcrerror mt-2 [&_a]:text-fcrbrown"
			role="none"
			onclick={(event): void => {
				// Prevent reloading the page when clicking on the link that changes the URL search params
				if (event.target instanceof HTMLAnchorElement && event.target.getAttribute("href")?.startsWith("?")) {
					event.preventDefault();
					history.pushState(null, "", event.target.getAttribute("href"));
					// Dispatch popstate event so that the URL change is detected
					window.dispatchEvent(new PopStateEvent("popstate"));
				}
			}}
		>
			<Formatted text={error} />
		</p>
	{:else if mode === "new-password"}
		<p class="text-sm mt-2">
			<Formatted text={translate("newPasswordRequired")} />
			<button
				class="text-fcrbrown underline"
				type="button"
				onclick={(e): void => {
					const form = e.currentTarget.closest("form");
					form?.reset();
					mode = "login";
				}}
			>
				<Formatted text={translate("cancelPasswordReset")} />
			</button>
		</p>
	{/if}
</Form>
<div class="mt-6 text-base md:text-1.5xl text-center font-medium">
	<Formatted text={translate("noAccount")} />
	<button
		class="text-fcrbrown underline"
		type="button"
		onclick={async (): Promise<void> => {
			await Auth.openAuthenticationModal(AuthenticationModalKind.Register, {});
		}}
	>
		<Formatted text={translate("create")} />
	</button>
</div>
